import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {createError, useAsyncData} from '#imports';
import {useErrorHandler} from '@malt/utils';
import type {LegalAgreementContentResponse, LegalAgreementLanguage, LegalAgreementType} from '@api';
import {FetchError, LegalAgreementAdminApi} from '@api';
import {apiConfiguration} from '~/configuration/apiConfiguration';

export const useLegalAgreementsStore = defineStore('legal-agreements', () => {
    const legalAgreementAdminApi = new LegalAgreementAdminApi(apiConfiguration);
    const {handleFatalError} = useErrorHandler();

    const isLoading = ref(false);
    const legalAgreementContentResponse = ref<LegalAgreementContentResponse | null>(null);

    async function fetchVersionContent(agreementType: LegalAgreementType, versionId: string, language: LegalAgreementLanguage) {
        isLoading.value = true;
        legalAgreementContentResponse.value = null;

        try {
            const {data, error} = await useAsyncData(
                `legal-agreement-${agreementType}-${language}-${versionId}`,
                async () => {
                    try {
                        return await legalAgreementAdminApi.getLegalAgreementVersionContent({
                            agreementType,
                            versionId,
                            language,
                        });
                    } catch (error) {
                        if (error instanceof FetchError) {
                            const errors = error.getCauseData();
                            if (Array.isArray(errors) && errors.length > 0 && errors.every((item) => typeof item?.error === 'string')) {
                                throw createError({message: errors.map((item) => item.error).join('\n'), statusCode: error.statusCode ?? 500});
                            }
                        }
                        throw error;
                    }
                },
                {
                    deep: true,
                },
            );

            if (error.value) {
                handleFatalError(error.value.message, error.value.statusCode ?? 500);
                return;
            }

            if (data.value) {
                legalAgreementContentResponse.value = data.value;
            }
        } finally {
            isLoading.value = false;
        }
    }

    async function fetchDraftContent(agreementType: LegalAgreementType, language: LegalAgreementLanguage) {
        isLoading.value = true;
        legalAgreementContentResponse.value = null;

        try {
            const {data, error} = await useAsyncData(
                `legal-agreement-${agreementType}-${language}-draft`,
                async () => {
                    const agreement = await legalAgreementAdminApi.getLegalAgreement({agreementType});
                    const draftContent = agreement.draftContents.find((content) => content.language === language);
                    if (!draftContent) {
                        throw createError({message: `No draft content for ${language}`, statusCode: 404});
                    }
                    return {content: draftContent.content};
                },
                {
                    deep: true,
                },
            );

            if (error.value) {
                handleFatalError(error.value.message, error.value.statusCode ?? 500);
                return;
            }

            if (data.value) {
                legalAgreementContentResponse.value = data.value;
            }
        } finally {
            isLoading.value = false;
        }
    }

    const legalAgreementContent = computed<string>(() => legalAgreementContentResponse.value?.content ?? '');

    return {
        isLoading,
        legalAgreementContent,
        legalAgreementContentResponse,

        fetchDraftContent,
        fetchVersionContent,
    };
});
